import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { LocaleService } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { FeedAdvice } from '../../models/feed-advice.model';
import * as feedAdviceActions from '../../store/feed-advice.actions';
import { getFeedAdvices } from '../../store/feed-advice.selectors';

@Component({
  selector: 'app-feed-advice-overview',
  templateUrl: './feed-advice-overview.component.html',
  standalone: false,
})
export class FeedAdviceOverviewComponent implements AfterViewInit, OnDestroy {
  /* SELECTORS & DATA */
  feedAdvices$ = this.store.select(getFeedAdvices);

  /* INDICATORS */
  isAdvisor = false;

  /* SUBSCRIPTIONS */
  subAuthorization: Subscription;
  subFeedAdvices: Subscription;

  /* TEMPLATES */
  @ViewChild('columnNameTemplate') columnNameTemplate: TemplateRef<unknown>;
  @ViewChild('columnActiveTemplate') columnActiveTemplate: TemplateRef<unknown>;

  /* TABLE CONFIG */
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource([] as FeedAdvice[]),
    selectColumn: false,
    pagination: false,
    columns: [
      {
        id: 'name',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.name'),
      },
      {
        id: 'created',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.created'),
      },
      {
        id: 'updated',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.updated'),
      },
      {
        id: 'updatedAdvisorName',
        type: TableColumnTypes.STRING,
        label: this.translate('table-header.updatedAdvisorName'),
      },
      {
        id: 'isActive',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.isActive'),
      },
    ],

    actions: [
      {
        id: 'edit',
        label: this.translate('buttons.edit'),
        callback: (feedAdvice: FeedAdvice) => {
          if (this.isAdvisor) {
            this.store.dispatch(feedAdviceActions.editAdviceNavigation({ feedAdvice }));
          }
        },
        icon: 'edit',
        disabled: () => !this.isAdvisor,
      },
      {
        id: 'delete',
        label: this.translate('buttons.delete'),
        callback: (feedAdvice: FeedAdvice) => {
          if (this.isAdvisor) {
            this.store.dispatch(feedAdviceActions.deleteAdviceConfirm({ feedAdvice }));
          }
        },
        icon: 'delete',
        disabled: () => !this.isAdvisor,
      },
    ],
  };

  constructor(
    private readonly localeService: LocaleService,
    private readonly store: Store<RootState>,
  ) {
    // Set authorization roles indicators.
    this.subAuthorization = this.store
      .pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole))
      .subscribe((hasAtLeastAdvisorRole) => {
        this.isAdvisor = hasAtLeastAdvisorRole;
      });

    // Get feed-advices from state.
    this.subFeedAdvices = this.feedAdvices$.subscribe((feedAdvices) => {
      if (feedAdvices !== null && feedAdvices !== undefined && feedAdvices.length > 0) {
        this.tableConfig.dataSource.data = feedAdvices;
      } else {
        this.tableConfig.dataSource.data = [];
      }
    });
  }

  ngAfterViewInit() {
    // Set isActive column template.
    setTimeout(() => {
      const newConfig = { ...this.tableConfig };
      const columnName = newConfig.columns.find((c) => c.id === 'name');
      const columnActive = newConfig.columns.find((c) => c.id === 'isActive');

      columnName.template = this.columnNameTemplate;
      columnActive.template = this.columnActiveTemplate;
      this.tableConfig = newConfig;
    }, 50);
  }

  translate(key: string): string {
    return this.localeService.translate(`feed-advice.overview.${key}`);
  }

  onButtonCreate() {
    if (this.isAdvisor) {
      this.store.dispatch(feedAdviceActions.createFromReferenceAdviceNavigation());
    }
  }

  onToggleActive(feedAdvice: FeedAdvice) {
    if (this.isAdvisor) {
      const lFeedAdvice = { ...feedAdvice };
      lFeedAdvice.isActive = !feedAdvice.isActive;
      this.store.dispatch(feedAdviceActions.upsertFeedAdviceRequest({ feedAdvice: lFeedAdvice }));
    }
  }

  onViewFeedAdvice(feedAdvice: FeedAdvice) {
    this.store.dispatch(feedAdviceActions.viewAdviceNavigation({ feedAdvice: { ...feedAdvice } }));
  }

  ngOnDestroy(): void {
    if (this.subAuthorization) {
      this.subAuthorization.unsubscribe();
    }

    if (this.subFeedAdvices) {
      this.subFeedAdvices.unsubscribe();
    }
  }
}
